import React from 'react';
import {
    MultiSelectSection,
    IMultiSelectSectionFetchList,
    IExtendedSelectOption,
    PaginationData,
    ExamStatus,
    AdvancedTable,
    AdvancedTableHeader,
    AdvancedTableData,
    ExamType,
    MappedExamTypes,
    MappedExamStatuses,
    WarningModal,
    renderRow,
    CustomSwitch,
    ProtocolLink,
    Status,
} from 'components';
import { Color, ColorStatus, formatPrice } from '_helpers';
import { Button } from 'react-bootstrap';
import emptyPaymentIcon from 'components/assets/svg/empty_payment_icon.svg';
import styles from './ExamWithPayment.module.scss';
import moment from 'moment';

const ExamWithPayment = ({
    examsPaymentList,
    getExamsWithPaymentList,
    onPush,
    qecList,
    qualificationList,
    from,
    ecList,
    examsPaymentFilters,
    getExamDetailsWithPayment,
    examWithPaymentDetails,
    sendMarkAsPaid,
}: IProps) => {
    const initialState: IState = {
        paid: (examsPaymentFilters && examsPaymentFilters.paid) || undefined,
        examinationCenterIds: (examsPaymentFilters && examsPaymentFilters.examinationCenterIds) || [],
        qualificationIds: (examsPaymentFilters && examsPaymentFilters.qualificationIds) || [],
        qecIds: (examsPaymentFilters && examsPaymentFilters.qecIds) || [],
    };

    const [paid, setPaid] = React.useState(initialState.paid);
    const [qecIds, setQecIds] = React.useState(initialState.qecIds);
    const [qualificationIds, setQualificationIds] = React.useState(initialState.qualificationIds);
    const [examinationCenterIds, setExaminationCenterIds] = React.useState(initialState.examinationCenterIds);
    const [fetchProps, setFetchProps] = React.useState<IGetExamsPayment | undefined>(undefined);
    const [showModal, setShowModal] = React.useState(false);
    const [processing, setProcessing] = React.useState(false);
    const [processingPaid, setProcessingPaid] = React.useState(false);

    const tableHeaders: AdvancedTableHeader[] = [
        { fieldName: '', title: 'Дата и время', columnStyles: { minWidth: '11rem' } },
        { fieldName: '', title: 'Квалификация', columnStyles: { width: '100%' } },
        { fieldName: '', title: 'Номер счета', columnStyles: { minWidth: '7rem' } },
        { fieldName: '', title: 'Сумма счета', columnStyles: { minWidth: '7rem' } },
        { fieldName: '', title: 'ТИП ЭКЗАМЕНА', columnStyles: { minWidth: '9rem' } },
        { fieldName: '', title: 'Статус', columnStyles: { minWidth: '9rem' } },
    ];

    const examStatusOptions: IExtendedSelectOption[] = [
        { label: 'Все', value: FilterState.All },
        { label: 'Оплаченные', value: FilterState.Paid },
        { label: 'Не оплаченные', value: FilterState.NotPaid },
    ];

    const renderQualification = (exam: IExamWithPayment) => (
        <div className={styles.qualification}>
            <div className={styles.qualification__name}>
                {exam.qualificationTitle}
            </div>
            <div className={styles.qualification__centerName}>
                {exam.examinationCenterName}
            </div>
            <div className={styles.qualification__address}>
                {exam.address}
            </div>
        </div>
    );

    const getTableData = (): AdvancedTableData[] => (examsPaymentList && examsPaymentList.items ? examsPaymentList.items.map(item => ({
        id: item.id,
        columns: [
            item.date.format('DD.MM.YYYY в HH:mm'),
            renderQualification(item),
            `${item.accountNumber ? item.accountNumber : '-'}`,
            <>{formatPrice(item.totalAmount)}<div className={styles.amountDescription}>{item.amountDescription.split('\n').map((text, i) => (text ? <span key={`text_${i}`} className={styles.amountDescription__block}>{text}</span> : null))}</div></>,
            MappedExamTypes[item.type].text,
            <Status status={item.paid ? new ColorStatus(Color.Default, 'Оплачен') : new ColorStatus(Color.Orange, 'Не оплачен')} />,
        ],
    })) : []);

    const handleChooseExam = async (id?: string) => {
        if (!id || !examsPaymentList || processing) return;

        const exam = examsPaymentList.items.find(e => e.id === id);

        if (exam) {
            setProcessing(true);
            await getExamDetailsWithPayment(id);
            setShowModal(true);
            setProcessing(false);
        }
    };

    const onExamDetailsClick = () => {
        if (!examWithPaymentDetails) return;

        if (from) {
            onPush(`/exam/${examWithPaymentDetails.id}?from=${from}`);
        } else {
            onPush(`/exam/${examWithPaymentDetails.id}`);
        }
    };

    const getPaginationObject = () => {
        if (!examsPaymentList) return undefined;

        const { pageSize, page, total } = examsPaymentList;

        return { page, pageSize, total };
    };

    const handleChangePagination = (newPagination: PaginationData) => getExamsWithPaymentList({
        ...examsPaymentFilters,
        ...fetchProps,
        examinationCenterIds,
        qualificationIds,
        qecIds,
        paid,
        page: newPagination.page,
        pageSize: newPagination.perPage,
    });

    const fetchList = (data: IMultiSelectSectionFetchList) => {
        let checkPaid;
        if (data.filteredExamStatus as string === FilterState.NotPaid) {
            checkPaid = false;
        } else if (data.filteredExamStatus as string === FilterState.Paid) {
            checkPaid = true;
        }

        const props: IGetExamsPayment = {
            ...examsPaymentFilters,
            ...data,
            paid: checkPaid,
        };

        setFetchProps(props);
        setPaid(checkPaid);
        setQecIds(data.qecIds || []);
        setExaminationCenterIds(data.examinationCenterIds || []);
        setQualificationIds(data.qualificationIds || []);

        getExamsWithPaymentList({ ...props });
    };

    const getDefaultValue = () => {
        if (examsPaymentFilters && examsPaymentFilters.paid === true) {
            return FilterState.Paid;
        }

        if (examsPaymentFilters && examsPaymentFilters.paid === false) {
            return FilterState.NotPaid;
        }

        return FilterState.All;
    };

    const pagination = getPaginationObject();

    const onSwitchClick = async () => {
        if (sendMarkAsPaid && examWithPaymentDetails && !processingPaid) {
            const data: ISendMarkAsPaid = {
                examId: examWithPaymentDetails.id,
                body: {
                    paid: !examWithPaymentDetails.paid || false,
                },
            };

            setProcessingPaid(true);
            await sendMarkAsPaid(data);
            if (pagination) {
                await handleChangePagination({ ...pagination, perPage: pagination.pageSize, pages: pagination.total });
            }
            await getExamDetailsWithPayment(examWithPaymentDetails.id);
            setProcessingPaid(false);
        }
    };

    return (
        <>
            {examsPaymentList && examsPaymentList.items ? (
                <MultiSelectSection
                  fetchList={fetchList}
                  examSelectStatusName="ExamListFilters"
                  examStatusOptions={examStatusOptions}
                  ecList={ecList}
                  qualificationList={qualificationList}
                  defaultValue={getDefaultValue()}
                  defaultIds={{ examinationCenterIds, qecIds, qualificationIds }}
                  qecList={qecList}
                  defaultFrom={examsPaymentFilters ? examsPaymentFilters.from : undefined}
                  defaultTo={examsPaymentFilters ? examsPaymentFilters.to : undefined}
                />
            ) : null}

            <AdvancedTable
              data={getTableData()}
              tableHeaders={tableHeaders}
              handlePageChange={handleChangePagination}
              handleRowClick={handleChooseExam}
              placeholderTitle="Нет истории взаиморасчетов"
              placeholderText="Здесь будут отображаться вся история взаиморасчетов с СПКФР"
              placeholderIcon={emptyPaymentIcon}
              {...pagination}
            />

            <WarningModal
              title={examWithPaymentDetails ? examWithPaymentDetails.qualificationTitle : ''}
              show={showModal}
              loading={processing}
              onHide={() => setShowModal(false)}
              footerClassNames="p-0"
              bodyClassNames="pb-0"
              dialogClassName={styles.modalDialog}
              withoutCancelButton
              large
            >
                {examWithPaymentDetails ? (
                    <>
                        {renderRow('ТИП ЭКЗАМЕНА', MappedExamTypes[examWithPaymentDetails.type].text)}
                        {renderRow('ЦОК', examWithPaymentDetails.qualificationEvaluationCenterName)}
                        {renderRow('ЭЦ', examWithPaymentDetails.examinationCenterName)}
                        {renderRow('ДАТА', examWithPaymentDetails.date.format('DD.MM.YYYY'))}
                        {renderRow('ВРЕМЯ', examWithPaymentDetails.date.format('HH:mm по МСК'), 'mb-5')}

                        {renderRow('СТАТУС ОБРАБОТКИ', MappedExamStatuses[examWithPaymentDetails.status].text, 'mb-5')}

                        {renderRow('СОИСКАТЕЛЕЙ', examWithPaymentDetails.amountDescription.split('\n').map((text, i) => (text ? <span key={`text_${i}`} className="d-block">{text}</span> : null)))}
                        {renderRow('ЭКСПЕРТОВ', `${examWithPaymentDetails.numberOfExperts} по 0 ₽ = 0 ₽`)}
                        {renderRow('ИТОГО К ОПЛАТЕ', formatPrice(examWithPaymentDetails.totalAmount), 'mb-5')}

                        {renderRow('СЧЕТ НА ОПЛАТУ', examWithPaymentDetails && (
                            <ProtocolLink
                              url={examWithPaymentDetails.invoiceForPaymentUrl}
                              additionalLabel={`№ ${examWithPaymentDetails.accountNumber} от ${examWithPaymentDetails.date.format('DD.MM.YYYY')}`}
                            />
                        ))}
                        {renderRow('АКТ', examWithPaymentDetails && (
                            <ProtocolLink
                              url={examWithPaymentDetails.aktDocUrl}
                              additionalLabel={`№ ${examWithPaymentDetails.accountNumber} от ${examWithPaymentDetails.date.format('DD.MM.YYYY')}`}
                            />
                        ))}
                        {examWithPaymentDetails.aktExtraDocUrl && renderRow('ДОП. АКТ', examWithPaymentDetails && (
                            <ProtocolLink
                              url={examWithPaymentDetails.aktExtraDocUrl}
                              additionalLabel={`№ ${examWithPaymentDetails.accountNumber} от ${examWithPaymentDetails.date.format('DD.MM.YYYY')}`}
                            />
                        ), 'mb-5')},

                        {renderRow('СТАТУС ОПЛАТЫ ', examWithPaymentDetails && (
                            <CustomSwitch
                              onClick={onSwitchClick}
                              name="exam-payment-selector"
                              loading={processingPaid}
                              value={examWithPaymentDetails.paid}
                              status={examWithPaymentDetails.paid ? undefined : 'default'}
                            />
                        ), styles.switchRow)}

                        <Button
                          className="action-button"
                          variant="outline-secondary"
                          onClick={onExamDetailsClick}
                        >
                            Подробная информация об экзамене
                        </Button>
                    </>
                ) : null}
            </WarningModal>
        </>
    );
};

export default ExamWithPayment;

interface IProps {
    examsPaymentList: IExamWithPaymentList | null;
    examWithPaymentDetails: IExamWithPaymentDetails | null;
    qualificationList?: IQualificationInfoList | null;
    examsPaymentFilters?: IGetExamsPayment | null;
    qecList?: IQualificationEvaluationCenters | null;
    ecList?: IExaminationCentersList | null;
    from?: string;
    getExamsWithPaymentList: (data: IGetExamsPayment) => void;
    getExamDetailsWithPayment: (examId: string) => void;
    onPush: (key: string) => void;
    sendMarkAsPaid?: (body: ISendMarkAsPaid) => void;
}

interface IExamWithPaymentList {
    unpaidAmount: number;
    items: IExamWithPayment[];
    page: number;
    pageSize: number;
    total: number;
}

interface IExamWithPayment {
    totalAmount: number;
    paid: boolean;
    accountNumber?: string | undefined;
    accountDate?: moment.Moment | undefined;
    id: string;
    date: moment.Moment;
    amountDescription: string;
    qualificationId: string;
    examinationCenterId: string;
    qualificationTitle: string;
    qualificationLevel: string;
    occupationalStandardTitle: string;
    qualificationEvaluationCenterName: string;
    examinationCenterName: string;
    address: string;
    city: string;
    status: ExamStatus;
    statusChangeTime: moment.Moment;
    duration: number;
    price: number;
    recertification: boolean;
    type: ExamType;
    qualificationEvaluationCenterId: string;
    numberOfApplicants: number;
    protocolNumber?: number | undefined;
}

interface IGetExamsPayment {
    paid?: boolean | undefined;
    qualificationIds?: string[];
    examinationCenterIds?: string[];
    qecIds?: string[];
    page?: number;
    pageSize?: number;
    sortOrder?: boolean | undefined;
    sortField?: string;
    to?: moment.Moment;
    from?: moment.Moment;
}

interface IExaminationCentersList {
    items: IExaminationCenter[];
    page: number;
    pageSize: number;
    total: number;
}

interface IExaminationCenter {
    id: string;
    regNumber: string;
    name: string;
    fullName: string;
    city: string;
    address: string;
    default: boolean;
}

interface IQualificationEvaluationCenters {
    items: IQualificationEvaluationCenter[];
    page: number;
    pageSize: number;
    total: number;
}

interface IQualificationEvaluationCenter {
    id: string;
    regNumber: string;
    name: string;
    fullName: string;
    city: string;
}

interface IQualificationInfoList {
    items: IQualificationInfo[];
    page: number;
    pageSize: number;
    total: number;
}

interface IQualificationInfo {
    id: string;
    title: string;
    level: string;
    regNumber: string;
    occupationalStandardId: string;
    occupationalStandardTitle: string;
}

enum FilterState {
    All = 'All',
    Paid = 'paid',
    NotPaid = 'notPaid'
}

interface ITimeProps {
    from?: moment.Moment;
    to?: moment.Moment;
}

interface IState {
    qecIds: string[];
    qualificationIds: string[];
    examinationCenterIds: string[];
    paid?: boolean;
    fromTime?: moment.Moment;
    toTime?: moment.Moment;
}

interface IExamWithPaymentDetails {
    totalAmount: number;
    paid: boolean;
    amountDescription: string;
    accountNumber?: string | undefined;
    accountDate?: moment.Moment | undefined;
    aktDocUrl?: string | undefined;
    aktExtraDocUrl?: string | undefined;
    invoiceForPaymentUrl?: string | undefined;
    /** Список экспертов экзаменационной комиссии / подписантов протокола НОК */
    experts: IExamExpert[];
    /** Список подписантов протокола АК */
    akExperts: IExamExpert[];
    /** Список подписантов протокола совета */
    sovietExperts: IExamExpert[];
    actualStartTime?: moment.Moment | undefined;
    actualFinishTime?: moment.Moment | undefined;
    manualCheck: boolean;
    // examLogs: ExamLog[];
    nokProtocolUrl?: string | undefined;
    akProtocolUrl?: string | undefined;
    sovietProtocolUrl?: string | undefined;
    rules?: string | undefined;
    theoreticalQuestionsCount: number;
    theoreticalPartDuration: number;
    practicalQuestionsCount: number;
    practicalPartDuration: number;
    protocolDate?: moment.Moment | undefined;
    eventsState?: { [key: string]: number } | undefined;
    id: string;
    date: moment.Moment;
    qualificationId: string;
    examinationCenterId: string;
    qualificationTitle: string;
    qualificationLevel: string;
    occupationalStandardTitle: string;
    qualificationEvaluationCenterName: string;
    examinationCenterName: string;
    address: string;
    city: string;
    status: ExamStatus;
    statusChangeTime: moment.Moment;
    /** Продолжительность экзамена в минутах */
    duration: number;
    price: number;
    /** Переаттестация */
    recertification: boolean;
    type: ExamType;
    qualificationEvaluationCenterId: string;
    numberOfApplicants: number;
    numberOfExperts?: number;
    protocolNumber?: number | undefined;
}

interface IExamExpert {
    regNumber: string;
    expertId: string;
    fullName: string;
    email: string;
    phone: string;
    region: string;
    /** Эксперт доступен для подписи протокола */
    active: boolean;
    /** Протокол пописан экспертом */
    signed: boolean;
    chairman: boolean;
    acceptedInvite?: boolean | undefined;
    position?: string | undefined;
    signedCode?: string | undefined;
}

interface ISendMarkAsPaid {
    examId: string;
    body: IMarkAsPaid;
}

interface IMarkAsPaid {
    paid: boolean;
}
